import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { codebooksClient } from 'api';
import type {
  ApiError,
  Codebook,
  CodebookToAdd,
  CodebookToEdit,
  CustomCodebook,
  CustomCodebookItem,
  CustomCodebookItemToAdd,
  CustomCodebookItemToEdit,
  CustomCodebookToAdd,
  CustomCodebookToEdit,
  Group,
  GroupToAdd,
  GroupToEdit,
  GroupUsersToManage,
  Manufacturer,
  ManufacturerToAdd,
  ManufacturerToEdit,
  OrderRequestCategory,
  OrderRequestCategoryToAdd,
  OrderRequestCategoryToEdit,
  ServiceObjectCategoryToEdit,
  Skill,
  SkillToAdd,
  SkillUsersToManage,
  VatOption,
  VatOptionToAdd,
  VatOptionToEdit,
  WorkItem,
  WorkItemToAdd,
  WorkItemToEdit
} from 'common/models';
import { GLOBAL_QUERY_STALE_TIME } from 'common/queries';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';

import type { Codebooks } from './models';

export const CODEBOOKS_INITIAL_DATA = {
  customerTypes: [],
  customerCategories: [],
  serviceObjectCategories: [],
  serviceObjectCustomStates: [],
  orderCategories: [],
  interventionCategories: [],
  interventionCustomStates: [],
  materialCategories: [],
  serviceObjectManufacturers: [],
  materialSuppliers: [],
  workItems: [],
  vatOptions: [],
  customCodebooks: [],
  orderCustomStates: [],
  orderRequestCustomStates: [],
  calendarEventCategories: [],
  groups: [],
  skills: [],
  paymentTypes: [],
  orderRequestCategories: [],
  serviceObjectRevisionCategories: []
};

const codebooksKey = 'codebooks';

export const getCodebooksQueryKeyBuilder = () => [codebooksKey];

export const useGetCodebooksQuery = () => {
  return useQuery<Codebooks, ApiError>(
    getCodebooksQueryKeyBuilder(),
    () => codebooksClient.getCodebooksQuery(),
    {
      staleTime: GLOBAL_QUERY_STALE_TIME
      // initialData: CODEBOOKS_INITIAL_DATA
    }
  );
};

// Customer category

export const useAddCustomerCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(codebooksClient.addCustomerCategory, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && { ...oldData, customerCategories: [...oldData.customerCategories, data] }
      );
    }
  });
};

export const useEditCustomerCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { categoryId: string; values: CodebookToEdit }>(
    codebooksClient.editCustomerCategory,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              customerCategories: oldData.customerCategories.map((category) => {
                if (category.id === variables.categoryId) {
                  return { ...category, ...variables.values };
                }
                return category;
              })
            }
        );
      }
    }
  );
};

export const useDeleteCustomerCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteCustomerCategory, {
    onSuccess: (data, categoryId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            customerCategories: oldData.customerCategories.filter(
              (category) => category.id !== categoryId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Customer type

export const useAddCustomerTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(codebooksClient.addCustomerType, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) => oldData && { ...oldData, customerTypes: [...oldData.customerTypes, data] }
      );
    }
  });
};

export const useEditCustomerTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { typeId: string; values: CodebookToEdit }>(
    codebooksClient.editCustomerType,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              customerTypes: oldData.customerTypes.map((type) => {
                if (type.id === variables.typeId) {
                  return { ...type, ...variables.values };
                }
                return type;
              })
            }
        );
      }
    }
  );
};

export const useDeleteCustomerTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteCustomerType, {
    onSuccess: (data, typeId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            customerTypes: oldData.customerTypes.filter((type) => type.id !== typeId)
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Material category

export const useAddMaterialCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(codebooksClient.addMaterialCategory, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && { ...oldData, materialCategories: [...oldData.materialCategories, data] }
      );
    }
  });
};

export const useEditMaterialCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { categoryId: string; values: CodebookToEdit }>(
    codebooksClient.editMaterialCategory,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              materialCategories: oldData.materialCategories.map((type) => {
                if (type.id === variables.categoryId) {
                  return { ...type, ...variables.values };
                }
                return type;
              })
            }
        );
      }
    }
  );
};

export const useDeleteMaterialCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteMaterialCategory, {
    onSuccess: (data, categoryId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            materialCategories: oldData.materialCategories.filter(
              (category) => category.id !== categoryId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Material supplier

export const useAddMaterialSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(codebooksClient.addMaterialSupplier, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && { ...oldData, materialSuppliers: [...oldData.materialSuppliers, data] }
      );
    }
  });
};

export const useEditMaterialSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { supplierId: string; values: CodebookToEdit }>(
    codebooksClient.editMaterialSupplier,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              materialSuppliers: oldData.materialSuppliers.map((supplier) => {
                if (supplier.id === variables.supplierId) {
                  return { ...supplier, ...variables.values };
                }
                return supplier;
              })
            }
        );
      }
    }
  );
};

export const useDeleteMaterialSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteMaterialSupplier, {
    onSuccess: (data, supplierId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            materialSuppliers: oldData.materialSuppliers.filter(
              (supplier) => supplier.id !== supplierId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Service Object Category

export const useEditServiceObjectCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { categoryId: string; values: ServiceObjectCategoryToEdit }
  >(codebooksClient.editServiceObjectCategory, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCategories: oldData.serviceObjectCategories.map(
              (serviceObjectCategory) => {
                if (serviceObjectCategory.id === variables.categoryId) {
                  return { ...serviceObjectCategory, ...variables.values };
                }
                return serviceObjectCategory;
              }
            )
          }
      );
    }
  });
};

// Service Object Manufacturer

export const useAddServiceObjectManufacturerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Manufacturer, ApiError, ManufacturerToAdd>(
    codebooksClient.addServiceObjectManufacturer,
    {
      onSuccess: (data) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              serviceObjectManufacturers: [...oldData.serviceObjectManufacturers, data]
            }
        );
      }
    }
  );
};

export const useEditServiceObjectManufacturerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { manufacturerId: string; values: ManufacturerToEdit }>(
    codebooksClient.editServiceObjectManufacturer,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              serviceObjectManufacturers: oldData.serviceObjectManufacturers.map((manufacturer) => {
                if (manufacturer.id === variables.manufacturerId) {
                  return { ...manufacturer, ...variables.values };
                }
                return manufacturer;
              })
            }
        );
      }
    }
  );
};

export const useDeleteServiceObjectManufacturerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteServiceObjectManufacturer, {
    onSuccess: (data, manufacturerId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectManufacturers: oldData.serviceObjectManufacturers.filter(
              (manufacturer) => manufacturer.id !== manufacturerId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Work Item

export const useAddWorkItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<WorkItem, ApiError, WorkItemToAdd>(codebooksClient.addWorkItem, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            workItems: [...oldData.workItems, data]
          }
      );
    }
  });
};

export const useEditWorkItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { workItemId: string; values: WorkItemToEdit }>(
    codebooksClient.editWorkItem,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              workItems: oldData.workItems.map((workItem) => {
                if (workItem.id === variables.workItemId) {
                  return { ...workItem, ...variables.values };
                }
                return workItem;
              })
            }
        );
      }
    }
  );
};

export const useDeleteWorkItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteWorkItem, {
    onSuccess: (data, workItemId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            workItems: oldData.workItems.filter((workItem) => workItem.id !== workItemId)
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Vat Option

export const useAddVatOptionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<VatOption, ApiError, VatOptionToAdd>(codebooksClient.addVatOption, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            vatOptions: [...oldData.vatOptions, data]
          }
      );
    }
  });
};

export const useEditVatOptionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { vatOptionId: string; values: VatOptionToEdit }>(
    codebooksClient.editVatOption,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              vatOptions: oldData.vatOptions.map((vatOption) => {
                if (vatOption.id === variables.vatOptionId) {
                  return { ...vatOption, ...variables.values };
                }
                return vatOption;
              })
            }
        );
      }
    }
  );
};

export const useDeleteVatOptionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteVatOption, {
    onSuccess: (data, vatOptionId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            vatOptions: oldData.vatOptions.filter((vatOption) => vatOption.id !== vatOptionId)
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Payment type

export const useAddCompanyPaymentTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(codebooksClient.addPaymentType, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            paymentTypes: [...oldData.paymentTypes, data]
          }
      );
    }
  });
};

export const useEditCompanyPaymentTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      paymentTypeId: string;
      values: CodebookToEdit;
    }
  >(codebooksClient.editPaymentType, {
    onSuccess: (data, { values, paymentTypeId }) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            paymentTypes: oldData.paymentTypes.map((paymentType) => {
              if (paymentType.id === paymentTypeId) {
                return { ...paymentType, ...values };
              }
              return paymentType;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyPaymentTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deletePaymentType, {
    onSuccess: (_, paymentTypeId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            paymentTypes: oldData.paymentTypes.filter(
              (paymentType) => paymentType.id !== paymentTypeId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Order Category

export const useEditOrderCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { categoryId: string; values: CodebookToEdit }>(
    codebooksClient.editOrderCategory,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              orderCategories: oldData.orderCategories.map((orderCategory) => {
                if (orderCategory.id === variables.categoryId) {
                  return { ...orderCategory, ...variables.values };
                }
                return orderCategory;
              })
            }
        );
      }
    }
  );
};

// Service Object Custom State

export const useAddServiceObjectCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>({
    mutationFn: codebooksClient.addServiceObjectCustomState,
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCustomStates: [...oldData.serviceObjectCustomStates, data]
          }
      );
    }
  });
};

export const useEditServiceObjectCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { serviceObjectCustomStateId: string; values: CodebookToEdit }
  >({
    mutationFn: codebooksClient.editServiceObjectCustomState,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCustomStates: oldData.serviceObjectCustomStates.map(
              (serviceObjectCustomState) =>
                serviceObjectCustomState.id === variables.serviceObjectCustomStateId
                  ? { ...serviceObjectCustomState, ...variables.values }
                  : serviceObjectCustomState
            )
          }
      );
    }
  });
};

export const useDeleteServiceObjectCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>({
    mutationFn: codebooksClient.deleteServiceObjectCustomState,
    onSuccess: (data, serviceObjectCustomStateId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCustomStates: oldData.serviceObjectCustomStates.filter(
              (serviceObjectCustomState) =>
                serviceObjectCustomState.id !== serviceObjectCustomStateId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Order Custom State

export const useAddOrderCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(codebooksClient.addOrderCustomState, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            orderCustomStates: [...oldData.orderCustomStates, data]
          }
      );
    }
  });
};

export const useEditOrderCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { orderCustomStateId: string; values: CodebookToEdit }>(
    codebooksClient.editOrderCustomState,
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              orderCustomStates: oldData.orderCustomStates.map((orderCustomState) => {
                if (orderCustomState.id === variables.orderCustomStateId) {
                  return { ...orderCustomState, ...variables.values };
                }
                return orderCustomState;
              })
            }
        );
      }
    }
  );
};

export const useDeleteOrderCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteOrderCustomState, {
    onSuccess: (data, orderCustomStateId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            orderCustomStates: oldData.orderCustomStates.filter(
              (orderCustomState) => orderCustomState.id !== orderCustomStateId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Order Request Custom State

export const useAddOrderRequestCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(
    codebooksClient.addOrderRequestCustomState,
    {
      onSuccess: (data) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              orderRequestCustomStates: [...oldData.orderRequestCustomStates, data]
            }
        );
      }
    }
  );
};

export const useEditOrderRequestCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { orderRequestCustomStateId: string; values: CodebookToEdit }
  >(codebooksClient.editOrderRequestCustomState, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            orderRequestCustomStates: oldData.orderRequestCustomStates.map(
              (orderRequestCustomState) => {
                if (orderRequestCustomState.id === variables.orderRequestCustomStateId) {
                  return { ...orderRequestCustomState, ...variables.values };
                }
                return orderRequestCustomState;
              }
            )
          }
      );
    }
  });
};

export const useDeleteOrderRequestCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteOrderRequestCustomState, {
    onSuccess: (data, orderRequestCustomStateId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            orderRequestCustomStates: oldData.orderRequestCustomStates.filter(
              (orderRequestCustomState) => orderRequestCustomState.id !== orderRequestCustomStateId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Intervention Category

export const useEditInterventionCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { interventionCategoryId: string; values: CodebookToEdit }
  >(codebooksClient.editInterventionCategory, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            interventionCategories: oldData.interventionCategories.map((interventionCategory) => {
              if (interventionCategory.id === variables.interventionCategoryId) {
                return {
                  ...interventionCategory,
                  ...variables.values
                };
              }
              return interventionCategory;
            })
          }
      );
    }
  });
};

// Intervention Custom State

export const useAddInterventionCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(
    codebooksClient.addInterventionCustomState,
    {
      onSuccess: (data) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              interventionCustomStates: [...oldData.interventionCustomStates, data]
            }
        );
      }
    }
  );
};

export const useEditInterventionCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { interventionCustomStateId: string; values: CodebookToEdit }
  >(codebooksClient.editInterventionCustomState, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            interventionCustomStates: oldData.interventionCustomStates.map(
              (interventionCustomState) => {
                if (interventionCustomState.id === variables.interventionCustomStateId) {
                  return { ...interventionCustomState, ...variables.values };
                }
                return interventionCustomState;
              }
            )
          }
      );
    }
  });
};

export const useDeleteInterventionCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteInterventionCustomState, {
    onSuccess: (data, interventionCustomStateId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            interventionCustomStates: oldData.interventionCustomStates.filter(
              (interventionCustomState) => interventionCustomState.id !== interventionCustomStateId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Calendar Event Categories

export const useAddCalendarEventCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, CodebookToAdd>(codebooksClient.addCalendarEventCategory, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            calendarEventCategories: [...oldData.calendarEventCategories, data]
          }
      );
    }
  });
};

export const useEditCalendarEventCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { calendarEventCategoryId: string; values: CodebookToEdit }
  >(codebooksClient.editCalendarEventCategory, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            calendarEventCategories: oldData.calendarEventCategories.map(
              (calendarEventCategory) => {
                if (calendarEventCategory.id === variables.calendarEventCategoryId) {
                  return { ...calendarEventCategory, ...variables.values };
                }
                return calendarEventCategory;
              }
            )
          }
      );
    }
  });
};

export const useDeleteCalendarEventCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteCalendarEventCategory, {
    onSuccess: (data, calendarEventCategoryId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            calendarEventCategories: oldData.calendarEventCategories.filter(
              (calendarEventCategory) => calendarEventCategory.id !== calendarEventCategoryId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Order Request Categories

export const useAddOrderRequestCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<OrderRequestCategory, ApiError, OrderRequestCategoryToAdd>(
    codebooksClient.addOrderRequestCategory,
    {
      onSuccess: (data) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              orderRequestCategories: [...oldData.orderRequestCategories, data]
            }
        );
      }
    }
  );
};

export const useEditOrderRequestCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { orderRequestCategoryId: string; values: OrderRequestCategoryToEdit }
  >(codebooksClient.editOrderRequestCategory, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            orderRequestCategories: oldData.orderRequestCategories.map((orderRequestCategory) => {
              if (orderRequestCategory.id === variables.orderRequestCategoryId) {
                return { ...orderRequestCategory, ...variables.values };
              }
              return orderRequestCategory;
            })
          }
      );
    }
  });
};

export const useDeleteOrderRequestCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteOrderRequestCategory, {
    onSuccess: (data, calendarEventCategoryId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            calendarEventCategories: oldData.calendarEventCategories.filter(
              (calendarEventCategory) => calendarEventCategory.id !== calendarEventCategoryId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Custom Codebook

export const useAddCustomCodebookItemBySlugMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CustomCodebookItem,
    ApiError,
    { slug: string; values: CustomCodebookItemToAdd }
  >(codebooksClient.addCustomCodebookItemAutoComplete, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: oldData.customCodebooks.map((customCodebook) => {
              if (customCodebook.slug === variables.slug) {
                return {
                  ...customCodebook,
                  items: [...customCodebook.items, data]
                };
              }

              return customCodebook;
            })
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useAddCustomCodebookMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<CustomCodebook, ApiError, CustomCodebookToAdd>(
    codebooksClient.addCustomCodebook,
    {
      onSuccess: (data) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              customCodebooks: [...oldData.customCodebooks, data]
            }
        );
      }
    }
  );
};

export const useEditCustomCodebookMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { customCodebookId: string; values: CustomCodebookToEdit }
  >(codebooksClient.editCustomCodebook, {
    onSuccess: (data, { customCodebookId, values }) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: oldData.customCodebooks.map((x) => {
              if (x.id === customCodebookId) {
                return {
                  ...x,
                  ...values
                };
              }

              return x;
            })
          }
      );
    }
  });
};

export const useDeleteCustomCodebookMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteCustomCodebook, {
    onSuccess: (data, customCodebookId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: oldData.customCodebooks.filter((x) => x.id !== customCodebookId)
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useAddCustomCodebookItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<CustomCodebookItem, ApiError, { id: string; values: CustomCodebookItemToAdd }>(
    codebooksClient.addCustomCodebookItem,
    {
      onSuccess: (data, { id }) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              customCodebooks: oldData.customCodebooks.map((x) => {
                if (x.id === id) {
                  return {
                    ...x,
                    items: [...x.items, data]
                  };
                }

                return x;
              })
            }
        );
      }
    }
  );
};

export const useEditCustomCodebookItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { customCodebookId: string; itemId: string; values: CustomCodebookItemToEdit }
  >(codebooksClient.editCustomCodebookItem, {
    onSuccess: (data, { customCodebookId, itemId, values }) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: oldData.customCodebooks.map((x) => {
              if (x.id === customCodebookId) {
                return {
                  ...x,
                  items: x.items.map((item) => {
                    if (item.id === itemId) {
                      return {
                        ...item,
                        ...values
                      };
                    }

                    return item;
                  })
                };
              }

              return x;
            })
          }
      );
    }
  });
};

export const useDeleteCustomCodebookItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { customCodebookId: string; itemId: string }>(
    codebooksClient.deleteCustomCodebookItem,
    {
      onSuccess: (data, { customCodebookId, itemId }) => {
        queryClient.setQueryData<Codebooks | undefined>(
          getCodebooksQueryKeyBuilder(),
          (oldData) =>
            oldData && {
              ...oldData,
              customCodebooks: oldData.customCodebooks.map((x) => {
                if (x.id === customCodebookId) {
                  return {
                    ...x,
                    items: x.items.filter((x) => x.id !== itemId)
                  };
                }

                return x;
              })
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Group

export const useAddGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Group, ApiError, GroupToAdd>(codebooksClient.addGroup, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            groups: [...oldData.groups, data]
          }
      );
    }
  });
};

export const useEditGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      groupId: string;
      values: GroupToEdit;
    }
  >(codebooksClient.editGroup, {
    onSuccess: (data, { values, groupId }) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            groups: oldData.groups.map((group) => {
              if (group.id === groupId) {
                return { ...group, ...values };
              }
              return group;
            })
          }
      );
    }
  });
};

export const useDeleteGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteGroup, {
    onSuccess: (data, groupId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            groups: oldData.groups.filter((group) => group.id !== groupId)
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useEditGroupMembersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      groupId: string;
      values: GroupUsersToManage;
    }
  >(codebooksClient.manageGroupUsers, {
    onSuccess: (data, { values, groupId }) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            groups: oldData.groups.map((group) => {
              if (group.id === groupId) {
                return { ...group, users: values.users };
              }
              return group;
            })
          }
      );
    }
  });
};

// Skill

export const useAddSkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Skill, ApiError, SkillToAdd>(codebooksClient.addSkill, {
    onSuccess: (data) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            skills: [...oldData.skills, data]
          }
      );
    }
  });
};

export const useEditSkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      skillId: string;
      values: CodebookToEdit;
    }
  >(codebooksClient.editSkill, {
    onSuccess: (data, { values, skillId }) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            skills: oldData.skills.map((skill) => {
              if (skill.id === skillId) {
                return { ...skill, ...values };
              }
              return skill;
            })
          }
      );
    }
  });
};

export const useDeleteSkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(codebooksClient.deleteSkill, {
    onSuccess: (data, skillId) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            skills: oldData.skills.filter((skill) => skill.id !== skillId)
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useEditSkillMembersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      skillId: string;
      values: SkillUsersToManage;
    }
  >(codebooksClient.manageSkillUsers, {
    onSuccess: (data, { values, skillId }) => {
      queryClient.setQueryData<Codebooks | undefined>(
        getCodebooksQueryKeyBuilder(),
        (oldData) =>
          oldData && {
            ...oldData,
            skills: oldData.skills.map((skill) => {
              if (skill.id === skillId) {
                return { ...skill, users: values.users };
              }
              return skill;
            })
          }
      );
    }
  });
};
